import React from "react";
import { graphql, navigate } from "gatsby";
import styled from "@emotion/styled";
import { Box, Flex, Heading, Text } from "@theme-ui/components";
import { MDXProvider } from "@mdx-js/react";
import { MDXRenderer } from "gatsby-plugin-mdx";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Layout, SEO } from "../../components";
import { NoteBanner, HeaderWithIcon, DocImage, Note } from "../../components/shortcodes";
import { NewsletterFormSection } from "../../components/resource";
import Header from "../../components/resource/Header";
import { ResourceDownloadCard, InfoTile } from "../../components/molecules";
import { colors, typography } from "../../styles/theme";
import { Icon } from "../../components/atoms";
import { Container } from "../../components/organisms";

const shortcodes = { NoteBanner, HeaderWithIcon, DocImage, Note };

const MdxContent = ({ body }) => {
  return (
    <MDXProvider components={shortcodes}>
      <MDXRenderer>{body}</MDXRenderer>
    </MDXProvider>
  );
};

const styles = {
  bottomLabel: { ...typography.textSm, color: colors.black, pl: "0.5rem", },
  mainContainer:{
    mt: "1.5rem",
    mx: ["1.06rem", "1.06rem", "2rem", "2rem"],
    gap: "6rem",
    ...typography.textMd400,
    alignItems: "flex-start",
    justifyContent: "center"
  },
  content:{
    width: "100%",
    justifyContent: "center",
    display: ["flex", "flex", "flex", "none"],
  }
};

const BottomLabel = ({ topic }) => (
  <Box>
    <Icon name="Tag" color={colors.black} size={16} />
    <Text sx={styles.bottomLabel }>{topic}</Text>
  </Box>
);
const DownloadableResourcePage = ({
  data: { downloadableResource, moreTemplates, randomTemplates },
  location: { href, search },
  pageContext: { regionPath, regionCode },
}) => {
  const {
    name,
    title,
    subTitle,
    ctaText,
    bannerImage,
    headerNode,
    bodyNode,
    footerNode,
    resourceType,
    attachmentUrl,
    attachment,
  } = downloadableResource;
  const templatesList = moreTemplates?.nodes?.length
    ? moreTemplates.nodes
    : randomTemplates.nodes;

  let resourceUrl = ``;
  if (attachmentUrl) {
    resourceUrl = attachmentUrl;
  } else if (attachment && attachment.url) {
    resourceUrl = `${attachment.url}?dl=${attachment.filename}`;
  }
  const formCode = regionCode === "ME" ? "WF_NEWSLETTER_TEMPLATE_ME" : "WF_NEWSLETTER_TEMPLATE";
	const pageCode = "TEMPLATE";
	const pageName = regionCode === "ME" ? "Template Page - ME" : "Template Page";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (resourceUrl !== "") {
      navigate(
        regionPath
          ? `/${regionPath}/downloadable-resource/thank-you/`
          : "/downloadable-resource/thank-you/",
        {
          state: {
            resourceUrl: resourceUrl,
            resourceName: name,
            resourceType: resourceType?.name,
            isEmailSent: false,
            templatesList: templatesList,
            regionPath,
          },
        }
      );
    }
  };
  return (
    <Layout region={regionCode}>
      <Flex
        sx={styles.mainContainer}
      >
        <Container variant="pillar" p={[0, 0, 0, 0]} m={[0, 0, 0, 0]}>
          <Flex sx={{ flexDirection: "column" }}>
            <Header
              title={title}
              subTitle={subTitle}
              customStyles={{ mb: "3rem" }}
              url={href}
              imageSrc={bannerImage}
            />
            {bannerImage && <GatsbyImage
              image={getImage(bannerImage)}
              alt={`${title}-${bannerImage?.alt}`}
              style={{
                borderRadius: "32px",
                width: "100%",
              }}
            />}
            <StyledBox sx={{ ...typography.textMd400, mt: "3rem" }}>
              {headerNode ? <MdxContent body={headerNode.childMdx.body} /> : null}
              {bodyNode ? <MdxContent body={bodyNode.childMdx.body} /> : null}
            </StyledBox>
            <Flex
              sx={styles.content}
            >
              <ResourceDownloadCard
                iconName="Download"
                title={ctaText}
                buttonLabel={"Download for FREE"}
                theme="secondaryBlue"
                onBtnClick={handleSubmit}
              />
            </Flex>
            {footerNode && <MdxContent body={footerNode.childMdx.body} />}
          </Flex>
        </Container>

        <ResourceDownloadCard
          iconName="Download"
          title={ctaText}
          buttonLabel={"Download for FREE"}
          theme="secondaryBlue"
          customStyles={{ display: ["none", "none", "none", "block"], minWidth: '19.0625rem' }}
          onBtnClick={handleSubmit}
        />
      </Flex>
      <Box sx={{ mb: "7rem" }}>
        {templatesList?.length > 0 && (
          <Box my="6.25rem">
            <Heading
              as="h2"
              sx={{ ...typography.h2, textAlign: "center", mb: "3rem" }}
            >
              More Templates
            </Heading>
            <Flex
              sx={{
                justifyContent: "center",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              {templatesList.map(
                ({ title, slug, subTitle, bannerImage, topic }, i) => (
                  <InfoTile
                    key={`recent-template-${i}`}
                    image={getImage(bannerImage)}
                    imageAltText={bannerImage.alt}
                    title={title}
                    content={subTitle}
                    hasBorder={false}
                    url={regionPath ? `/${regionPath}/${slug}/` : `/${slug}/`}
                    bottomLabel={topic?.name && <BottomLabel topic={topic?.name} />}
                  />
                )
              )}
            </Flex>
          </Box>
        )}
      </Box>
      <NewsletterFormSection
        pageUrl={href}
				formName={formCode}
				pageCode={pageCode}
				pageName={pageName}
				search={search}
      />
    </Layout>
  );
};

const StyledBox = styled(Box)`
  ul.downld-rsrc-list {
    margin-bottom: 3rem;
    list-type-style: none;
    margin-left: -15px;
  }
  ul.downld-rsrc-list li:before {
    content: "";
    padding-right: 0px;
  }

  ul.downld-rsrc-list li {
    background: url("https://www.datocms-assets.com/40521/1700199379-list-icon.png")
    no-repeat left center;
    list-style: none;
    vertical-align: middle;
    padding-left: 30px;
    margin-bottom: 0.75rem;
  }
  @media (max-width: 600px) {
    padding: 0px 12px;
  }
`;

export const Head = ({
  data: { downloadableResource },
  location: { pathname },
}) => {
  const { hreflang, regionCode, seo } = downloadableResource;
  const supportedRegions = regionCode && regionCode.split(", ");
  const hrefLang =
    hreflang ||
    (supportedRegions?.length > 0 &&
      supportedRegions.includes("IN") &&
      supportedRegions.includes("ME"));
  return <SEO datoSeo={seo} pathname={pathname} hrefLang={hrefLang} availableInRegions={regionCode}  />;
};

export const query = graphql`
  query DownloadableResourceQuery($objectId: String!, $topic: String!, $regionCode: String!) {
    greytalk: file(relativePath: { eq: "resources/greytalk-logo.png" }) {
      childImageSharp {
        gatsbyImageData(width: 200, layout: FIXED)
      }
    }
    downloadableResource: datoCmsDownloadableResource(id: { eq: $objectId }) {
      originalId
      hreflang
      regionCode
      name
      title
      subTitle
      ctaText
      headerNode {
        childMdx {
          body
        }
      }
      bodyNode {
        childMdx {
          body
        }
      }
      footerNode {
        childMdx {
          body
        }
      }
      bannerImage {
        gatsbyImageData(width: 800, layout: CONSTRAINED)
      }
      seo {
        title
        description
        image {
					url
				}
      }
      resourceType {
        name
      }
      attachment {
        filename
        url(imgixParams: {})
      }
      attachmentUrl
    }
    moreTemplates: allDatoCmsDownloadableResource(
      filter: { topic: { name: { eq: $topic } }, id: { ne: $objectId }, regionCode: { eq: $regionCode } }
      limit: 3
    ) {
      nodes {
        title
        topic {
          name
        }
        subTitle
        slug
        bannerImage {
          alt
          gatsbyImageData(width: 342, height: 170, layout: CONSTRAINED)
        }
      }
    }
    randomTemplates: allDatoCmsDownloadableResource(
      limit: 3
      filter: { id: { ne: $objectId }, regionCode: { eq: $regionCode } }
    ) {
      nodes {
        title
        topic {
          name
        }
        subTitle
        slug
        bannerImage {
          alt
          gatsbyImageData(width: 342, height: 170, layout: CONSTRAINED)
        }
      }
    }
  }
`;

export default DownloadableResourcePage;
